/**
 * Copyright (C) 2026 alaraajavamma <aki@urheiluaki.fi>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 *
 * SPDX-License-Identifier: GPL-3.0-or-later
 */

import React from 'react';
import { PriceTagItem, LayoutConfig } from '../types';
import { IntersportLogo } from './IntersportLogo';

interface PriceTagProps {
  item: PriceTagItem;
  config: LayoutConfig;
}

/**
 * PriceTag Component
 * Highly optimized, printable retail price tag system with professional typographical scales.
 * Supports multiple layouts (A4, A5, A6 vertical/horizontal and custom 25x5cm Shoe strip "Kenkäluiska").
 * Built to automatically fit content and scale font size depending on price lengths.
 */
/**
 * PriceTag-komponentti
 * 
 * Tehokkaasti optimoitu, tulostusystävällinen ja ammattimainen hintalappu- ja etiketöintijärjestelmä.
 * Tukee useita eri koko- ja asetteluformaatteja (A4, A5, A6 pysty/vaaka sekä Kenkäluiska).
 * Sovittaa tekstien ja hintojen fonttikoot automaattisesti merkkipituuksien mukaan välttäen ylivuotoja.
 */
export const PriceTag: React.FC<PriceTagProps> = ({ item, config }) => {
  const { title, brand, category, priceNormal, priceSale, extraBadge } = item;
  const { preset, showCutLines, showBrand, showCategory, showBadge } = config;

  /**
   * Hintojen muotoilutyökalu:
   * Korvaa pisteet pilkuilla, trimmaa tyhjät pois ja poistaa ylimääräiset €-merkit,
   * koska asettelussa käytetään erikseen tyyliteltyjä valuuttasymboleita.
   */
  const formatPrice = (p: string) => {
    if (!p) return '';
    let val = p.replace(/\./g, ',').trim();
    val = val.replace(/€/g, '').trim();
    return val;
  };

  const formattedNormal = formatPrice(priceNormal);
  const isPercentageDisc = !!item.isPercentage && !!priceSale;
  
  let formattedSale = '';
  let currencySymbol = '€';
  
  if (isPercentageDisc) {
    const cleanedPercent = priceSale.replace(/%/g, '').replace(/-/g, '').trim();
    formattedSale = cleanedPercent ? `-${cleanedPercent}` : '';
    currencySymbol = '%';
  } else {
    formattedSale = formatPrice(priceSale);
    currencySymbol = '€';
  }
  
  const hasSale = isPercentageDisc ? !!formattedSale : (formattedSale && formattedSale !== formattedNormal);

  /**
   * Hintojen dynaaminen fonttikoko (getPriceSizeClass):
   * Pienentää fonttikokoa automaattisesti silloin, kun hinnan merkkijono on poikkeuksellisen pitkä.
   * Tämä takaa, etteivät pitkät hinnat (esim. 5 tai yli 5 merkkiä) rivity tai vuoda yli reunoista.
   */
  const getPriceSizeClass = (length: number, format: 'A4' | 'A5' | 'A6' | 'LANDSCAPE' | 'SHOE') => {
    if (format === 'A4') {
      if (length > 5) return 'text-[170px]';
      if (length === 5) return 'text-[200px]';
      return 'text-[245px]';
    }
    if (format === 'A5') {
      if (length > 5) return 'text-[105px]';
      if (length === 5) return 'text-[130px]';
      return 'text-[155px]';
    }
    if (format === 'A6') {
      if (length > 5) return 'text-[70px]';
      if (length === 5) return 'text-[88px]';
      return 'text-[104px]';
    }
    if (format === 'LANDSCAPE') {
      if (length > 5) return 'text-[62px]';
      if (length === 5) return 'text-[76px]';
      return 'text-[92px]';
    }
    if (format === 'SHOE') {
      if (length > 5) return 'text-[44px]';
      if (length === 5) return 'text-[50px]';
      return 'text-[60px]';
    }
    return '';
  };

  const activePriceLength = hasSale ? formattedSale.length : formattedNormal.length;

  /**
   * Tuotenimen dynaaminen fonttikoko (getTitleSizeClass):
   * Tarkistaa sekä koko nimen pituuden että yksittäisen pisimmän sanan pituuden.
   * Ratkaisee ylivuoto-ongelmat pitkien tuotenimien tai yhdyssanojen (esim. "JUOKSUKENGÄT") kohdalla.
   */
  const getTitleSizeClass = (t: string, format: 'A4' | 'A5' | 'A6') => {
    const words = t.split(/\s+/);
    const longestWordLength = Math.max(...words.map(w => w.length), 0);
    const totalLength = t.length;

    if (format === 'A4') {
      if (longestWordLength >= 14) return 'text-[48px]';
      if (longestWordLength >= 12) return 'text-[54px]';
      if (longestWordLength >= 11) return 'text-[60px]';
      if (longestWordLength >= 9) return 'text-[68px]';
      if (longestWordLength >= 8) return 'text-[76px]';
      if (totalLength > 25) return 'text-[68px]';
      if (totalLength > 18) return 'text-[76px]';
      return 'text-[88px]';
    }
    if (format === 'A5') {
      if (longestWordLength >= 14) return 'text-[28px]';
      if (longestWordLength >= 12) return 'text-[32px]';
      if (longestWordLength >= 11) return 'text-[34px]';
      if (longestWordLength >= 9) return 'text-[38px]';
      if (longestWordLength >= 8) return 'text-[42px]';
      if (totalLength > 25) return 'text-[38px]';
      return 'text-[48px]';
    }
    // A6
    if (longestWordLength >= 14) return 'text-[18px]';
    if (longestWordLength >= 12) return 'text-[20px]';
    if (longestWordLength >= 11) return 'text-[22px]';
    if (longestWordLength >= 9) return 'text-[26px]';
    if (totalLength > 25) return 'text-[26px]';
    return 'text-[32px]';
  };

  /**
   * Leikkausviivat ja kulmamerkit tulostusta varten:
   * (Päivitetty: Nämä on poistettu yksittäisiltä lapuilta, sillä leikkausviivat piirretään logiikan mukaan
   * suoraan koko A4-arkin tasolla. Tämä poistaa päällekkäiset paksut viivat ja sovittaa ne täsmälleen keskelle).
   */
  const renderCropMarks = () => {
    return null;
  };

  /**
   * Huomiomerkkien (kampanjatyypit) dynaaminen väritys:
   * Valitsee automaattisen taustavärin avainsanan mukaan tai käyttää mukautettua HEX-koodia.
   * Suorittaa reaaliaikaisen YIQ-luminanssianalyysin valitulle värille ja muuttaa tekstin
   * automaattisesti vaaleaksi tai tummaksi saavutettavuussuositusten (WCAG) mukaan.
   */
  const getBadgeColors = () => {
    // Determine the active hexadecimal color with fallback
    const activeBg = item.extraBadgeBg || (extraBadge ? (
      (() => {
        const text = extraBadge.toLowerCase();
        if (text.includes('tarjous') || text.includes('ale') || text.includes('-') || text.includes('kampanja') || text.includes('%')) {
          return '#E30613';
        } else if (text.includes('kanta-asiakas') || text.includes('plussa') || text.includes('club') || text.includes('jäsen') || text.includes('etu')) {
          return '#005aa3';
        } else if (text.includes('uutuus')) {
          return '#10b981';
        }
        return '#18181b';
      })()
    ) : '');

    if (activeBg) {
      const cleanHex = activeBg.replace('#', '');
      let isLight = false;
      if (cleanHex.length === 6) {
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        isLight = yiq >= 128; // Light base Hex values automatically yield dark grey text
      }
      return { 
        bgClass: '', 
        textClass: isLight ? 'text-slate-950' : 'text-white', 
        style: { backgroundColor: activeBg } 
      };
    }

    return { bgClass: 'bg-zinc-850', textClass: 'text-white', style: {} };
  };

  const badgeColors = getBadgeColors();

  // Preset Size Grid Configuration Definitions: Map physical dimensions (A4, A5, A6, shoe stripe)
  let containerSizeClass = "";
  const baseClasses = "relative bg-white text-black flex flex-col justify-between overflow-visible select-none prnt-tag-box ";

  if (preset === 'A4_PORTRAIT') {
    containerSizeClass = "w-[210mm] h-[295mm] p-16 shadow-xs border-transparent";
  } else if (preset === 'A5_PORTRAIT') {
    containerSizeClass = "w-[148.5mm] h-[208mm] p-10";
  } else if (preset === 'A6_PORTRAIT') {
    containerSizeClass = "w-[105mm] h-[147mm] p-6";
  } else if (preset === 'A6_LANDSCAPE') {
    containerSizeClass = "w-[148.5mm] h-[103.5mm] p-6 flex-col justify-between";
  } else if (preset === 'SHOE_STRIP') {
    containerSizeClass = "w-[249mm] h-[49mm] px-6 py-1.5 flex-row items-center justify-between gap-4";
  }

  // Visual border outlining controls
  // Disable individual borders for 'SHOE_STRIP' to avoid subpixel doubling/overlapping with absolute sheet-level cutting lines
  const cutLineStyle = showCutLines 
    ? (preset === 'SHOE_STRIP' 
        ? "border border-transparent" 
        : "border border-solid border-slate-200/60 print:border-transparent")
    : "border border-transparent";

  // =========================================================================
  // TEMPLATE 1: SHOE STRIP (KENKÄLUISKA) 25cm x 4.6cm
  // Perfect narrow format optimized for standard shoe presentation slots
  // =========================================================================
  if (preset === 'SHOE_STRIP') {
    return (
      <div 
        className={`${baseClasses} ${containerSizeClass} ${cutLineStyle} transition-all duration-200 shadow-xs print:shadow-none`}
        id={`tag-${item.id}`}
      >
        {renderCropMarks()}

        {/* Brand Intersport Logo Zone - Significantly larger! */}
        <div className="flex items-center gap-2 max-w-[28%] shrink-0 pl-1">
          <IntersportLogo symbolOnly={true} symbolSize={54} />
        </div>

        {/* Product specs center section (Enlarged and optimized layout space) */}
        <div className="flex-1 flex flex-col justify-center leading-tight overflow-hidden pl-4">
          <div className="flex items-center gap-2.5">
            {showBrand && brand && (
              <span className="text-[14px] font-black uppercase tracking-tight text-[#005aa3]">
                {brand}
              </span>
            )}
            {showCategory && category && (
              <span className="text-[11.5px] font-bold text-slate-400 uppercase tracking-wider truncate">
                {category}
              </span>
            )}
          </div>
          <h2 className="text-xl font-black text-slate-950 tracking-tight leading-tight mt-1 line-clamp-1 max-w-[450px]">
            {title}
          </h2>
          {item.description && (
            <p className="text-[11px] font-medium text-slate-500 line-clamp-2 tracking-tight mt-1 leading-tight">
              {item.description}
            </p>
          )}
        </div>

        {/* Pricing & dynamic campaign badge on the far right (Badge is positioned on the left side of the price block, higher up) */}
        <div className="flex items-start justify-end gap-3.5 max-w-[54%] shrink-0 pl-3 border-l border-slate-100 h-full py-2">
          {showBadge && extraBadge && (
            <div 
              style={badgeColors.style} 
              className={`${badgeColors.bgClass} ${badgeColors.textClass} font-extrabold uppercase rounded-sm shadow-md tracking-widest text-center flex items-center justify-center whitespace-nowrap py-2 px-4 text-[13px] leading-none self-start mt-0.5`}
            >
              {extraBadge}
            </div>
          )}

          <div className="flex flex-col items-end justify-center leading-none self-center h-full">
            {hasSale ? (
              <div className="flex flex-col items-end">
                {formattedNormal && (
                  <span className="text-[11px] text-[#A3A3A3] font-bold relative line-through scale-90 origin-right">
                    Norm. {formattedNormal} €
                  </span>
                )}
                <div className="flex items-start text-[#E30613] font-black tracking-tighter leading-none mt-0.5">
                  <span className={getPriceSizeClass(activePriceLength, 'SHOE')}>{formattedSale}</span>
                  <span className="text-2xl font-bold ml-0.5 mt-2">{currencySymbol}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-start text-slate-950 font-black tracking-tighter leading-none">
                <span className={getPriceSizeClass(activePriceLength, 'SHOE')}>{formattedNormal}</span>
                <span className="text-2xl font-bold ml-0.5 mt-2">€</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // TEMPLATE 2: A6 LANDSCAPE (A6 VAAKA) 14.85cm x 10.5cm
  // Perfect horizontal layout for shelves and hanging setups
  // =========================================================================
  if (preset === 'A6_LANDSCAPE') {
    return (
      <div 
        className={`${baseClasses} ${containerSizeClass} ${cutLineStyle} transition-all duration-200 shadow-xs print:shadow-none`}
        id={`tag-${item.id}`}
      >
        {renderCropMarks()}

        {/* 1. BRAND HEADER BLOCK: Store identification logo at top spanning the width, centered */}
        <div className="w-full flex justify-center border-b border-slate-100 pb-2 mb-2">
          <IntersportLogo height="h-[36px]" />
        </div>

        {/* 2. BODY CONTENT AREA */}
        <div className="flex-1 flex flex-col justify-center w-full">
          {/* Campaign Badge below logo, above category, wide and sleek without early wrapping */}
          {showBadge && extraBadge && (
            <div className="w-full flex justify-start mb-2.5">
              <div 
                style={badgeColors.style} 
                className={`${badgeColors.bgClass} ${badgeColors.textClass} font-extrabold uppercase rounded-sm shadow-md tracking-widest text-center flex items-center justify-center whitespace-nowrap px-4 py-1.5 text-xs w-auto max-w-full`}
              >
                {extraBadge}
              </div>
            </div>
          )}

          {showCategory && category && (
            <span className="text-[12.5px] font-bold tracking-widest text-[#005aa3] uppercase leading-none">
              {category}
            </span>
          )}
          
          {showBrand && brand && (
            <span className="text-[17px] font-black text-slate-800 uppercase leading-none tracking-tight mt-1">
              {brand}
            </span>
          )}

          <h2 className="text-xl font-extrabold text-[#020617] tracking-tight leading-snug mt-1 line-clamp-2">
            {title}
          </h2>
          {item.description && (
            <p className="text-[11px] font-medium text-slate-500 line-clamp-3 tracking-tight mt-0.5 leading-snug max-w-[85%]">
              {item.description}
            </p>
          )}
        </div>

        {/* Bottom row: store attribution on left, and massive price on bottom-right */}
        <div className="flex items-end justify-between border-t border-slate-100 pt-1.5 w-full mt-auto">
          {/* Bottom left store name */}
          <div className="pb-0.5">
            <span className="text-[8.5px] font-mono text-slate-400">Avoin yhtiö Urheiluaki</span>
          </div>

          {/* Bottom right price block */}
          <div className="flex flex-col items-end justify-end">
            {hasSale ? (
              <div className="flex flex-col items-end">
                {formattedNormal && (
                  <span className="text-[11px] text-slate-400 font-bold relative text-nowrap mb-0.5">
                    <span className="absolute left-0 right-0 top-1/2 border-t-2 border-red-500 scale-y-110"></span>
                    Norm. {formattedNormal} €
                  </span>
                )}
                <div className="flex items-start text-[#E30613] font-black tracking-tighter leading-none">
                  <span className={getPriceSizeClass(activePriceLength, 'LANDSCAPE')}>{formattedSale}</span>
                  <span className="text-2xl font-bold ml-0.5 mt-1.5">{currencySymbol}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-start text-slate-950 font-black tracking-tighter leading-none">
                <span className={getPriceSizeClass(activePriceLength, 'LANDSCAPE')}>{formattedNormal}</span>
                <span className="text-2xl font-bold ml-0.5 mt-1.5">€</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // TEMPLATE 3: STANDARD VERTICAL PRESETS (A4, A5, A6 PORTRAITS)
  // Stacked, premium presentation maximizing vertical and horizontal readability
  // =========================================================================
  const isA4 = preset === 'A4_PORTRAIT';
  const isA5 = preset === 'A5_PORTRAIT';

  return (
    <div 
      className={`${baseClasses} ${containerSizeClass} ${cutLineStyle} transition-all duration-200 shadow-xs print:shadow-none`}
      id={`tag-${item.id}`}
    >
      {renderCropMarks()}

      {/* 1. BRAND HEADER BLOCK: Store identification logo at top covering the width, centered */}
      <div className="w-full flex justify-center border-b border-slate-100 pb-5 mb-5 shrink-0">
        <IntersportLogo 
          height={
            isA4 ? 'h-[76px]' : 
            isA5 ? 'h-[50px]' : 
            'h-[34px]'
          } 
        />
      </div>

      {/* 2. BODY CONTENT AREA: Dynamic scale Category Path, Brand Title, & main Product Label */}
      <div className="my-auto flex flex-col gap-3.5 w-full font-sans">
        {/* Campaign Badge below logo, above category, wide and sleek without early wrapping */}
        {showBadge && extraBadge && (
          <div className="w-full flex justify-start mb-2">
            <div 
              style={badgeColors.style} 
              className={`${badgeColors.bgClass} ${badgeColors.textClass} font-extrabold uppercase rounded-sm shadow-md tracking-widest text-center flex items-center justify-center whitespace-nowrap
                ${isA4 ? (
                    'px-8 py-3 text-[1.65rem] w-auto max-w-full leading-none'
                  ) : isA5 ? (
                    'px-6 py-2.5 text-xl w-auto max-w-full leading-none'
                  ) : ( // A6 or other small formats
                    'px-4 py-1.5 text-xs w-auto max-w-full leading-none'
                  )}`}
            >
              {extraBadge}
            </div>
          </div>
        )}

        {/* Category pathway navigation details */}
        {showCategory && category && (
          <p className={`font-extrabold tracking-widest text-[#005aa3] uppercase leading-none
            ${isA4 ? 'text-3xl' : 
              isA5 ? 'text-xl' : 
              'text-sm'}`}
          >
            {category}
          </p>
        )}

        {/* Primary manufacturer / branding reference */}
        {showBrand && brand && (
          <p className={`font-black tracking-tight text-slate-800 leading-none uppercase
            ${isA4 ? 'text-[52px]' : 
              isA5 ? 'text-[36px]' : 
              'text-[24px]'}`}
          >
            {brand}
          </p>
        )}

        {/* Primary product identifying heading (High accessibility size multiplier) */}
        <h2 className={`font-extrabold text-slate-950 tracking-tight leading-tight mt-1 break-words hyphens-auto
          ${isA4 ? `${getTitleSizeClass(title, 'A4')} line-clamp-3` : 
            isA5 ? `${getTitleSizeClass(title, 'A5')} line-clamp-3` : 
            `${getTitleSizeClass(title, 'A6')} line-clamp-2 leading-none`}`}
        >
          {title}
        </h2>
        {item.description && (
          <p className={`font-medium text-slate-500 tracking-tight leading-normal mt-1
            ${isA4 ? 'text-2xl line-clamp-6 mt-3' : 
              isA5 ? 'text-lg line-clamp-4 mt-2' : 
              'text-xs line-clamp-3 mt-1'}`}
          >
            {item.description}
          </p>
        )}
      </div>

      {/* 3. TRANSACTIONAL FOOTER BLOCK: Massive Price on Right, location note bottom */}
      <div className={`w-full flex flex-col gap-4 border-t border-slate-100 ${isA4 ? 'pt-8' : isA5 ? 'pt-5' : 'pt-3'}`}>
        
        <div className="flex items-end justify-end w-full">
          {/* Slashed previous reference prices and massive bold final checkout prices */}
          <div className="text-right flex flex-col justify-end leading-none w-full">
            {hasSale ? (
              <div className="flex flex-col items-end w-full">
                {/* Sale original comparison price */}
                {formattedNormal && (
                  <span className={`text-slate-400 font-bold relative text-nowrap mb-1
                    ${isA4 ? 'text-3xl mb-3' : 
                      isA5 ? 'text-lg mb-2' : 
                      'text-sm'}`}
                  >
                    <span className="absolute left-0 right-0 top-1/2 border-t-2 border-red-500 scale-y-110"></span>
                    Norm. {formattedNormal} €
                  </span>
                )}
                
                {/* Active discounted/sale retail price */}
                <div className="flex items-start text-[#E30613] font-black tracking-tighter leading-none animate-fade-in">
                  <span className={
                    getPriceSizeClass(activePriceLength, isA4 ? 'A4' : isA5 ? 'A5' : 'A6')
                  }>
                    {formattedSale}
                  </span>
                  <span className={`font-bold ml-1 ${
                    isA4 ? 'text-[70px] mt-8' : 
                    isA5 ? 'text-[44px] mt-4' : 
                    'text-[30px] mt-2'
                  }`}>
                    {currencySymbol}
                  </span>
                </div>
              </div>
            ) : (
              /* Regular non-discounted default retail price */
              <div className="flex items-start justify-end text-slate-950 font-black tracking-tighter leading-none w-full animate-fade-in">
                <span className={
                  getPriceSizeClass(activePriceLength, isA4 ? 'A4' : isA5 ? 'A5' : 'A6')
                }>
                  {formattedNormal}
                </span>
                <span className={`font-bold ml-1 ${
                  isA4 ? 'text-[70px] mt-8' : 
                  isA5 ? 'text-[44px] mt-4' : 
                  'text-[30px] mt-2'
                }`}>
                  €
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Location identification footer */}
        <div className={`flex items-center justify-between gap-4 pt-1.5 border-t border-slate-50 w-full ${isA4 ? 'pt-4' : ''}`}>
          <span className={`text-slate-400 font-mono tracking-wider uppercase font-bold
            ${isA4 ? 'text-sm' : 'text-[10px]'}`}
          >
            Avoin yhtiö Urheiluaki
          </span>
        </div>
      </div>
    </div>
  );
};
